import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Check, ChevronsUpDown, PanelsTopLeft, PenLine, ShoppingBag, Plus, Settings } from "lucide-react";
import type { Area, AddRow, SwitcherRows } from "../lib/areas";
import type { SiteFeature } from "../lib/features";
import { Skeleton, SkeletonGroup } from "./Skeleton";
import { Spinner } from "./Spinner";
import { AreaError } from "./area/AreaError";

const AREA_ICON: Record<Area, ReactNode> = {
  website: <PanelsTopLeft size={18} aria-hidden="true" />,
  shop: <ShoppingBag size={18} aria-hidden="true" />,
  blog: <PenLine size={18} aria-hidden="true" />,
};

/** What an Add row looks like: its feature's own mark. The shop's entry sits beside the blog's. */
const ADD_ICON: Record<SiteFeature, ReactNode> = {
  blog: <Plus size={18} aria-hidden="true" />,
  shop: <Plus size={18} aria-hidden="true" />,
};

interface AreaSwitcherProps {
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  current: Area;
  rows: SwitcherRows;
  /** The two lines on the button: the area's name, then what it is. */
  label: { name: string; detail: string };
  siteName: string;
  hint: string | null;
  onPickArea: (area: Area) => void;
  onAdd: (feature: AddRow["feature"]) => void;
  onSiteSettings: () => void;
  /** The Site settings row's second line: the site's live state, like "tanah.stack.je · Free". */
  settingsDetail: string;
}

/**
 * A workspace-style switcher at the top of the drawer: the site, the area it's in, and a menu of the areas it has,
 * the ones it could add, and Site settings.
 */
export function AreaSwitcher({ current, rows, label, siteName, hint, onPickArea, onAdd, onSiteSettings, settingsDetail, loading, error, onRetry }: AreaSwitcherProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => { if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    // The current area's row takes focus, so the arrow keys and Enter work at once.
    requestAnimationFrame(() => root.current?.querySelector<HTMLElement>('[role="menuitemradio"][aria-checked="true"]')?.focus());
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  const close = (returnFocus = true) => { setOpen(false); if (returnFocus) button.current?.focus(); };
  const choose = (action: () => void) => () => { setOpen(false); action(); };

  return <div className="bw-areas" ref={root}>
    <button ref={button} type="button" className="bw-areas-button" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} aria-label={`Switch area. Current: ${label.name}`} onClick={() => setOpen(!open)}>
      <span className="bw-areas-tile" data-area={current} aria-hidden="true">{AREA_ICON[current]}</span>
      <span className="bw-areas-text"><strong>{label.name}</strong><small>{label.detail}</small></span>
      {loading ? <Spinner /> : <ChevronsUpDown size={16} aria-hidden="true" />}
    </button>
    {open && <div id={menuId} className="bw-areas-menu" role="menu" aria-label={`Areas of ${siteName}`} onKeyDown={(event) => {
      if (event.key === "Escape") { event.stopPropagation(); close(); return; }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      event.preventDefault();
      const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]')];
      const at = items.indexOf(document.activeElement as HTMLElement);
      items[(at + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
    }}>
      <p className="bw-areas-site">{siteName}</p>
      {rows.areas.map((row) => <button key={row.id} type="button" role="menuitemradio" aria-checked={row.id === current} className="bw-areas-row" onClick={choose(() => onPickArea(row.id))}>
        <span className="bw-areas-tile" data-area={row.id} aria-hidden="true">{AREA_ICON[row.id]}</span>
        <span className="bw-areas-text"><strong>{row.name}</strong><small>{row.description}</small></span>
        {row.note && <span className="bw-areas-note">{row.note}</span>}
        <Check className="bw-areas-check" size={16} aria-hidden="true" />
      </button>)}
      {loading && <SkeletonGroup label="Loading site areas" className="bw-loading-areas"><Skeleton height={44} /><Skeleton height={44} /></SkeletonGroup>}
      {error && <AreaError inline message={error} onRetry={onRetry} />}
      {!loading && !error && rows.adds.map((row) => <button key={row.feature} type="button" role="menuitem" className="bw-areas-row" data-add={row.feature} onClick={choose(() => onAdd(row.feature))}>
        <span className="bw-areas-tile" data-add aria-hidden="true">{ADD_ICON[row.feature]}</span>
        <span className="bw-areas-text"><strong>{row.name}</strong><small>{row.description}</small></span>
      </button>)}
      <hr />
      <button type="button" role="menuitem" className="bw-areas-row bw-areas-settings" onClick={choose(onSiteSettings)}>
        <Settings size={18} aria-hidden="true" />
        <span className="bw-areas-text"><strong>Site settings</strong><small>{settingsDetail}</small></span>
      </button>
      {hint && <p className="bw-areas-hint">{hint}</p>}
    </div>}
  </div>;
}
